import {
  Header,
  Sidenav,
  type SidenavNavItem,
  AccessGatewayIcon,
  AdminIcon,
  DashboardIcon,
  IntegrationsIcon,
  LifecycleIcon,
  PostureIcon,
} from "@repo/ui";
import { tenantApi, storeTokens } from "@repo/api";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactNode, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

/** Minimum ms the loading indicator stays visible before page reload. */
const MIN_SWITCH_DURATION = 3000;
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useViewport, ViewportProvider } from "../contexts/ViewportContext";
import { useGetCurrentUserRoles } from "./hooks/useGetCurrentUserRoles";
import { navLabels } from "./navLabels";

interface ShellLayoutProps {
  children: ReactNode;
}

function ShellLayoutInner({ children }: ShellLayoutProps) {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const intl = useIntl();
  const {
    isMenuPinned,
    isMenuExpanded,
    setMenuPinned,
    setMenuExpanded,
    headerRef,
  } = useViewport();

  const { data, isLoading } = useGetCurrentUserRoles();
  const isAdmin = data?.roles?.includes("ROLE_SAV_ISPMADMIN") ?? false;

  const { data: tenantsData } = useQuery({
    queryKey: ["tenants"],
    queryFn: () => tenantApi.getTenants(),
    staleTime: 5 * 60 * 1000,
  });

  const currentTenantId =
    localStorage.getItem("tenantId") ?? tenantsData?.primaryTenantId;

  const switchStartRef = useRef<number | null>(null);
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false);

  const { mutate: switchTenant } = useMutation({
    mutationFn: (tenantId: string) => tenantApi.selectTenant(tenantId),
    onMutate: () => {
      switchStartRef.current = Date.now();
      setIsSwitchingTenant(true);
    },
    onSuccess: (response) => {
      storeTokens({
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
      });
      localStorage.setItem("tenantId", response.selectedTenantId);
      const elapsed = Date.now() - (switchStartRef.current ?? Date.now());
      const wait = Math.max(0, MIN_SWITCH_DURATION - elapsed);
      // Keep isSwitchingTenant true — page.reload() resets all state
      setTimeout(() => window.location.reload(), wait);
    },
    onError: () => {
      setIsSwitchingTenant(false);
    },
  });

  const navItems = useMemo<SidenavNavItem[]>(
    () => [
      {
        navItem: "dashboard",
        label: intl.formatMessage(navLabels.dashboard),
        icon: <DashboardIcon />,
        section: "workspace",
      },
      {
        navItem: "posture",
        label: intl.formatMessage(navLabels.posture),
        icon: <PostureIcon />,
        section: "workspace",
        subNav: [
          {
            subNavItem: "identities",
            label: intl.formatMessage(navLabels.identities),
            path: "/identities",
          },
          {
            subNavItem: "policies",
            label: intl.formatMessage(navLabels.policies),
            path: "/policies",
          },
        ],
      },
      {
        navItem: "integrations",
        label: intl.formatMessage(navLabels.integrations),
        icon: <IntegrationsIcon />,
        section: "workspace",
      },
      {
        navItem: "lifecycle",
        label: intl.formatMessage(navLabels.lifecycle),
        icon: <LifecycleIcon />,
        section: "workspace",
        subNav: [
          {
            subNavItem: "agents",
            label: intl.formatMessage(navLabels.agents),
            path: "/agent",
          },
        ],
      },
      {
        navItem: "access-gateway",
        label: intl.formatMessage(navLabels["access-gateway"]),
        icon: <AccessGatewayIcon />,
        section: "workspace",
        path: "/agent-gateway",
      },
      ...(isAdmin
        ? [
            {
              navItem: "admin",
              label: intl.formatMessage(navLabels.admin),
              icon: <AdminIcon />,
              section: "system" as const,
              subNav: [
                {
                  subNavItem: "ispm-users",
                  label: intl.formatMessage(navLabels["ispm-users"]),
                },
                {
                  subNavItem: "settings",
                  label: intl.formatMessage(navLabels.settings),
                  children: [
                    {
                      subNavItem: "sso-settings",
                      label: intl.formatMessage(navLabels["sso-settings"]),
                    },
                  ],
                },
              ],
            },
          ]
        : []),
    ],
    [intl, isAdmin],
  );

  return (
    <div className="min-h-screen flex bg-page-bg">
      <div className="sticky top-0 self-start h-screen shrink-0">
        <Sidenav
          navItems={navItems}
          isLoading={isLoading}
          isMenuPinned={isMenuPinned}
          onPinnedChange={setMenuPinned}
          isMenuExpanded={isMenuExpanded}
          onExpandedChange={setMenuExpanded}
        />
      </div>
      <div
        className="flex min-w-0 flex-1 flex-col"
        style={{ background: "var(--color-page-subtle)" }}
      >
        <div
          ref={headerRef}
          className="sticky top-0 z-[1008] shrink-0 bg-[var(--color-background-neutral-subtlest)]"
        >
          <Header
            theme={theme}
            onToggleTheme={toggleTheme}
            onLogout={logout}
            showLogo={!(isMenuPinned || isMenuExpanded)}
            showHeaderActions={false}
            tenants={tenantsData?.tenants}
            currentTenantId={currentTenantId}
            onTenantSelect={switchTenant}
            isSwitchingTenant={isSwitchingTenant}
          />
        </div>
        <main className="min-w-0 flex-1 pb-8">{children}</main>
      </div>
    </div>
  );
}

export function ShellLayout({ children }: ShellLayoutProps) {
  return (
    <ViewportProvider>
      <ShellLayoutInner>{children}</ShellLayoutInner>
    </ViewportProvider>
  );
}
